import { Country } from "react-phone-number-input";
import Nullable = Common.Nullable;
import { FpPaymentMethod } from "@fortius/prime-ui";

export interface ILanguage {
  id: number;
  code: string;
  label: string;
}
export interface ICurrency {
  code: string;
  symbol: string;
  locale: string;
}
export interface IFeeBand {
  currency: string;
  description: string;
  feeBandId: number;
  turnoverFrom: number;
  turnoverTo: number;
}

export interface ICountry {
  countryCode: ECountryCode;
  countryName: string;
}

export interface IBrand {
  code: EBrandCode;
  name: string;
}

export interface IBenefitIcon {
  icon: string;
  tooltip: string;
  altText: string;
}

export interface IAddress {
  line1: string;
  line2: string;
  city: string;
  postcode: string;
  county?: string;
  country: string;
  countryCode?: string;
}

export type TOrganisationType =
  | "belgium buyer"
  | "france buyer"
  | "belgium supplier"
  | "france supplier"
  | "supplier"
  | "buyer"
  | "both";

export interface ISignupStep {
  id: ESignupStep;
  name: Nullable<string>;
  path: string;
  visible: boolean;
}

export type IUser = {
  emailAddress: string;
  firstName: string;
  lastName: string;
};

export interface ISubscription {
  productSalesTypeIds: number[];
}

export interface ICompany {
  name: string;
  tradeName: string;
  registrationNumber: Nullable<string>;
  companyStatus: Nullable<string>;
  registeredCompanyAddress: IAddress;
  organisationTypes: TOrganisationType[];
  organisationCompanyTypeId: ECompanyType;
  feeBandId: number;
  vatNumber: Nullable<string>;
  isGuest: boolean;
  organisationAccountId: Nullable<number>;
  entityId: Nullable<number>;
  phoneNumber: Nullable<IPhoneNumber>;
  establishmentNumber: Nullable<string>;
  businessLine: Nullable<number>;
}

 export interface IPaymentConfig {
   termsAndConditionsLink: string;
   termOfUseLink: string;
   privacyPolicyLink: string;
   stripePrivacyPolicyLink: string;
   billingEmail: string;
   stripePublishableKey: string;
 }

export interface IProductPricingSummary {
  name: string;
  productId: number;
  productSalesTypeId: number;
  hasTrial: boolean;
  trialNetAmount: Nullable<number>;
  trialVat: Nullable<number>;
  trialGrossAmount: Nullable<number>;
  trialDurationInDays: Nullable<number>;
  trialProductSalesTypeId: Nullable<number>;
  netAmount: Nullable<number>;
  vat: Nullable<number>;
  grossAmount: Nullable<number>;
  startInDays: Nullable<number>;
  todayGrossAmount: Nullable<number>;
  futureGrossAmount: Nullable<number>;
  joiningFee: Nullable<IJoiningFeeSummary>;
}

export interface IJoiningFeeSummary {
  name: string;
  productId: number;
  productSalesTypeId: number;
  netAmount: Nullable<number>;
  vat: Nullable<number>;
  grossAmount?: Nullable<number>;
}

export interface ICheckoutProduct {
  productId: number;
  name: string;
  netAmount: Nullable<number>;
  taxRate: number;
  currency: string;
  productSalesTypeId: number;
  futureNetAmount: Nullable<number>;
  futureProductSalesTypeId: Nullable<number>;
  pricingSummary: IProductPricingSummary;
}

export interface IBilling {
  title: string;
  telephone: string;
  address: IAddress;
  firstName: string;
  lastName: string;
  emailAddress: string;
}

export interface IProductContentOffer {
  title: string;
  description: string;
  terms: string;
}

export interface IProductContentQuote {
  text: string;
  authorName: string;
  authorPosition: string;
  authorCompany: string;
}

export interface IProductContentSidebar {
  quote: IProductContentQuote;
}

export interface IProductContent {
  productSalesTypeId: number;
  productSiloId: number;
  siloName: string;
  name: string;
  description: string;
  shortDescription: string;
  isRecommended: boolean;
  icon: string;
  primaryColor: string;
  secondaryColor: string;
  benefits: string[];
  benefitIcons: IBenefitIcon[];
  offer: IProductContentOffer;
  sidebar: IProductContentSidebar;
}

export interface ICheckoutProductContent {
  productSiloId: number;
  productSiloName: string;
  productId: number;
  productName: string;
  iconUrl: string;
  iconAlt: string;
  description: string;
}

export interface IUserDetailsFormContent {
  body: string;
}

export interface IUserDetailsSidebarContent {
  title: string;
  body: string;
  productSiloName: string;
  image: {
    url: string;
    altText: Nullable<string>;
  };
}

export interface IProductsMetadata {
  invitedByOrganisationName?: string;
  showBuyerName?: "true" | "false";
}
export interface IBankTransferDetails {
  title: string;
  body: string;
  bankname: string;
  accountName: string;
  sortCode: string;
  accountNumber: string;
  reference: string;
}

export interface ICommunity {
  communityAccountId: number;
  code: ECommunityCode;
}

export type TPhoneCountryCode = Country;

export interface IPhoneNumber {
  countryCode: TPhoneCountryCode;
  countryCallingCode: string;
  nationalNumber: string;
  internationalNumber: string;
}

export interface IRelationsPlanContent {
  label: string;
  maxRelations: Nullable<number>;
  includesCollection: boolean;
  includesDiffusion: boolean;
  description: string;
}

export interface IRelationsCollectionContent {
  title: string;
  fullTitle: string;
  description: string;
  benefits: string[];
  thumbnailUrl: string;
  imageUrl: string;
}

export interface IOptOutOptionContent {
  key: string;
  productId: number;
  productSalesTypeId: Nullable<number>;
  title: string;
  description: string;
  iconUrl: string;
}

export interface IBusinessLine {
  code: number;
  labelTranslationKey: string;
}

export interface IVatCountry {
  countryCode: string;
  countryName: string;
}

export enum ECommunityCode {
  UK = "uk",
  FR = "fr"
}

export enum ESignupStep {
  UserDetails = 1, // BasicInformation
  CompanyDetails = 2, // OrganisationDetails
  ProductsDetails = 3, // SubscriptionSelection
  Billing = 4, // BillingInformation
  SignupReason = 5, // AdditionalInformationOrReason
  Checkout = 6, // PaymentDetails
  Relations = 20, // Relations
  OptOut = 21 // OptOut
}

export enum ECountryCode {
  GB = "GB",
  FR = "FR",
  BE = "BE",
  PT = "PT",
  ES = "ES"
}

export enum ECompanyType {
  LimitedLiabilityPartnership = 1,
  PublicLimitedCompany = 2,
  PrivateLimitedCompany = 3,
  Partnership = 4,
  UnlimitedCompany = 5,
  SoleTrader = 6,
  Other = 7
}

export enum EPaymentMethod {
  Card = 0,
  DirectDebit = 1,
  Sepa = 2,
  BankTransfer = 3
}

export enum EStripePaymentMethod {
  Card = 0,
  DirectDebit = 1,
  Sepa = 2
}

export enum EPaymentIntentStatus {
  Succeeded = 0,
  InProgress = 1,
  Failed = 2
}

export enum EBrandCode {
  Constructionline = "cl",
  BuildersProfile = "bp",
  Facilitiesline = "fl",
  AttestationLegale = "alg",
  Viaco = "viaco",
  Hiveo = "hiveo",
  Marketplace = "mp-fr",
  Actradis = "actradis"
}

export enum ESignupChannel {
  Public = "Public",
  Private = "InsideSales"
}

export enum ELanguageCode {
  EN = "en",
  FR = "fr",
  NL = "nl",
  ES = "es",
  PT = "pt"
}

export const STRIPE_PAYMENT_METHOD_MAP: Record<EStripePaymentMethod, FpPaymentMethod> = {
  [EPaymentMethod.DirectDebit]: FpPaymentMethod.BacsDebit,
  [EPaymentMethod.Card]: FpPaymentMethod.Card,
  [EPaymentMethod.Sepa]: FpPaymentMethod.SepaDebit
};
